import * as React from 'react'
import { Link } from 'react-router-dom'

import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { Tooltip } from '@factorialco/f0-react/dist/experimental'
import { F0TagAlert, F0AvatarPerson, F0Icon } from '@factorialco/f0-react'
import { Clock } from '@factorialco/f0-react/icons/app'
import { PlanningBalancesQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import { hoursToDuration } from 'modules/shift_management/lib/hoursToDuration'
import { buildMinimumRestTooltipContent } from 'modules/shift_management/lib/minimumRestTooltip'

import BankOfHours from './BankOfHours'
import HoursToPlan from './HoursToPlan'
import PlannedHours from './PlannedHours'
import ShiftsRowCheckbox from './ShiftsRowCheckbox'

type TimePlanningBalance = NonNullable<
  PlanningBalancesQuery['timePlanning']['balancesConnection']['nodes']
>[number]

interface Props {
  shiftsRow: ShiftsRow
  balances?: TimePlanningBalance
  isOverMaximum: boolean
  plannedHoursTooltipCopy: string
}

const EmployeeInfoCell = ({
  shiftsRow,
  balances,
  isOverMaximum,
  plannedHoursTooltipCopy,
}: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ canManageShifts }] = usePermissions()

  const [isHovered, setIsHovered] = React.useState(false)
  const { viewOptions } = useCurrentViewOptions()
  const [{ view }] = useSchedule()
  const { navigation } = useNavigation()
  const {
    employee: { showRole, showPlanningBalance, showAvatar, showBankOfHours },
  } = viewOptions
  const hasBankOfHoursEnabled = hasFeatureFlag('dev_time_planning_bank_of_hours')
  const showMinRestAlerts = canManageShifts && hasFeatureFlag('dev_shift_management_min_rest_alert')

  const visibleRole = !!shiftsRow.job && showRole

  const minRestViolationsInRange = shiftsRow.minimumRestBetweenDaysViolationsInRange(
    view.firstDay,
    view.lastDay
  )
  const minRestViolations = showMinRestAlerts ? minRestViolationsInRange : []
  const minRestRequiredMinutes = shiftsRow.referenceContract?.minRestMinutesBetweenDays || 0
  const minRestRequiredDuration = hoursToDuration(minRestRequiredMinutes / 100)

  const plannedLine = isOverMaximum ? plannedHoursTooltipCopy : null
  const minRestHours = minRestRequiredDuration.hours || 0
  const minRestMinutes = minRestRequiredDuration.minutes || 0
  const minRestSingleHours = i18n.t(
    'shift_management.hours_information.tooltip.minimum_rest_between_days_single',
    {
      hours: minRestHours,
    }
  )
  const minRestSingleHoursMinutes = i18n.t(
    'shift_management.hours_information.tooltip.minimum_rest_between_days_single_hours_minutes',
    {
      hours: minRestHours,
      minutes: minRestMinutes,
    }
  )
  const minRestMultipleHours = i18n.t(
    'shift_management.hours_information.tooltip.minimum_rest_between_days_multiple',
    {
      hours: minRestHours,
    }
  )
  const minRestMultipleHoursMinutes = i18n.t(
    'shift_management.hours_information.tooltip.minimum_rest_between_days_multiple_hours_minutes',
    {
      hours: minRestHours,
      minutes: minRestMinutes,
    }
  )
  const hasMinutePart = minRestMinutes > 0
  const minRestSingleLine = hasMinutePart ? minRestSingleHoursMinutes : minRestSingleHours
  const minRestHeaderLine = hasMinutePart ? minRestMultipleHoursMinutes : minRestMultipleHours
  const minRestContent = buildMinimumRestTooltipContent(
    minRestViolations,
    minRestSingleLine,
    minRestHeaderLine
  )
  const alertsCount = (plannedLine ? 1 : 0) + minRestViolations.length
  const alertCountLabel = alertsCount > 9 ? '9+' : `${alertsCount}`
  const tooltipTextColor = 'grey000White'
  const singleAlertLine = plannedLine || minRestContent.header || minRestContent.items[0] || ''
  const tooltipContent =
    alertsCount === 1 ? (
      <Text.Normal size="200" color={tooltipTextColor}>
        {singleAlertLine}
      </Text.Normal>
    ) : (
      <Box flexDirection="column" gap="s4">
        {plannedLine && (
          <Box flexDirection="row" gap="s4" alignItems="flexStart">
            <Text.Normal size="200" color={tooltipTextColor}>
              •
            </Text.Normal>
            <Text.Normal size="200" color={tooltipTextColor}>
              {plannedLine}
            </Text.Normal>
          </Box>
        )}
        {minRestContent.header && (
          <Box flexDirection="row" gap="s4" alignItems="flexStart">
            <Text.Normal size="200" color={tooltipTextColor}>
              {minRestContent.header}
            </Text.Normal>
          </Box>
        )}
        {minRestContent.items.map((item, index) => (
          <Box
            key={index}
            flexDirection="row"
            gap="s4"
            alignItems="flexStart"
            paddingLeft={minRestContent.header ? 's16' : 's0'}
          >
            <Text.Normal size="200" color={tooltipTextColor}>
              •
            </Text.Normal>
            <Text.Normal size="200" color={tooltipTextColor}>
              {item}
            </Text.Normal>
          </Box>
        ))}
      </Box>
    )

  const [firstName, lastName] = shiftsRow.title.split(' ')

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" flexGrow minWidth="s0" gap="s8">
      <Box
        flexDirection="row"
        alignItems="flexStart"
        gap="s8"
        justifyContent="center"
        minWidth="s0"
        flexGrow
      >
        {showAvatar && (
          <F0AvatarPerson
            firstName={firstName || ''}
            lastName={lastName || ''}
            size="md"
            src={buildUrl(shiftsRow.avatar)}
          />
        )}
        <Box
          flexDirection="column"
          gap={visibleRole ? 's8' : 's0'}
          paddingLeft={showAvatar ? 's0' : 's8'}
          flexGrow
          minWidth="s0"
        >
          <Box
            flexDirection="column"
            justifyContent="center"
            height={!visibleRole && !showPlanningBalance ? 's32' : 'fit-content'}
            maxWidth="s140"
          >
            <Box flexDirection="row" alignItems="center" gap="s4">
              <Link
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                to={navigation.employees.id.fullPath({ id: shiftsRow.id })}
                style={{ textDecoration: 'none' }}
                target="_blank"
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s4"
                  minWidth="s0"
                  maxWidth="s140"
                  flexGrow
                >
                  <Text
                    size="200"
                    weight="medium"
                    color={isHovered ? 'viridian1000' : 'grey1000'}
                    ellipsis
                  >
                    {shiftsRow.title}
                  </Text>
                </Box>
              </Link>
              {alertsCount > 0 && (
                <Tooltip
                  /* eslint-disable-next-line no-type-assertion/no-type-assertion */
                  label={tooltipContent as unknown as string}
                >
                  <Box flexDirection="row" alignItems="center" gap="s4">
                    <F0TagAlert level="critical" text={alertCountLabel} />
                  </Box>
                </Tooltip>
              )}
            </Box>
            {visibleRole && (
              <Text size="100" color="grey800" ellipsis>
                {shiftsRow.job}
              </Text>
            )}
          </Box>
          <Box gap="s6">
            {showPlanningBalance && (
              <Box flexDirection="row" height="s16" alignItems="center" gap="s4">
                <F0Icon icon={Clock} size="xs" className="text-f1-icon-secondary" />
                <PlannedHours shiftsRow={shiftsRow} isOverMaximum={isOverMaximum} />
                {(shiftsRow.canManage || shiftsRow.id === currentEmployee?.id) && (
                  <>
                    <Text size="100" color="grey800">
                      /
                    </Text>

                    {balances ? (
                      <HoursToPlan balances={balances} />
                    ) : (
                      <Skeleton isLoading height="s16" width="s40" />
                    )}
                  </>
                )}
              </Box>
            )}
            {shiftsRow.canManage && hasBankOfHoursEnabled && showBankOfHours && (
              <BankOfHours shiftsRow={shiftsRow} />
            )}
          </Box>
        </Box>
      </Box>
      <ShiftsRowCheckbox shiftsRow={shiftsRow} />
    </Box>
  )
}

export default EmployeeInfoCell
